import { t, ta } from "../lib/sprache";

/**
 * Portal-Sperre nach Vertragsende (Inhaber 22.10.2026, 0425), Fachlogik ohne Oberfläche.
 *
 * Bis zum Wirksamwerden der Kündigung arbeitet die Agentur normal weiter. Ab dem Vertragsende ist das Portal gesperrt: statt des
 * Portals steht die Sperrseite „Dein Abo ist beendet“ mit Export (nur Admin), neuem Abo und Löschung. Die Datenbank sagt, wann der
 * Vertrag beendet ist (org_read_only_state.ended, app.org_contract_ended): gekündigt, Test ohne Abo abgelaufen, Laufzeit eines vom
 * Betrieb freigeschalteten Abos ohne Verlängerung abgelaufen. Pause, offene Zahlung und Sperre durch den Betrieb bleiben nur lesbar.
 */
export type PortalZustand = "locked" | "read_only" | "normal";

export function portalZustand(ro: { read_only?: boolean; reason?: string; ended?: boolean } | null | undefined): PortalZustand {
  if (!ro?.read_only) return "normal";
  return ro.ended ? "locked" : "read_only";
}

/** Seiten, die der Admin hinter der Sperre erreicht: Export, Abo & Tarif (neues Abo, Löschung, Rechtstexte), Hilfe */
export const SPERRE_WEGE = ["/agentur/einstellungen/export", "/agentur/einstellungen/abo", "/agentur/hilfe"] as const;
export function sperreErlaubt(pathname: string): boolean {
  return SPERRE_WEGE.some((w) => pathname === w || pathname.startsWith(`${w}/`));
}

/** Creator einer beendeten Agentur: nur Konto (eigener Daten-Download, AVV) und Abmelden */
export function creatorSperreErlaubt(pathname: string): boolean {
  return /^\/creator\/(konto|abmelden)(\/|$)/.test(pathname);
}

export interface SperreLage {
  reason: string | null | undefined;
  /** S-2: nie bezahlt (Test abgelaufen oder in der Testphase beendet; org_read_only_state.trial) */
  trial: boolean;
  /** Vertragsende (formatiert), sonst null */
  endeAm: string | null;
  /** Ende der Export-Phase (formatiert): nächster Schritt Reduktion bzw. Löschung */
  exportBis: string | null;
  /** schon auf die aufbewahrungspflichtigen Belege reduziert */
  reduziert: boolean;
  /** Belege bis (formatiert), nach der Reduktion */
  belegeBis: string | null;
  /** keine aufbewahrungspflichtigen Belege: nach der Export-Phase wird alles gelöscht */
  allesLoeschen: boolean;
  /** vorzeitige Löschung beantragt */
  loeschungBeantragt: boolean;
}

export function sperreTitel(reason: string | null | undefined, trial = false): string {
  return trial || reason === "trial_expired" ? t("Dein Test ist beendet", "Your trial has ended") : t("Dein Abo ist beendet", "Your subscription has ended");
}

/** Erster Satz: das Portal ist gesperrt (seit wann), die Daten sind da bzw. reduziert */
export function sperreSatz(l: SperreLage): string {
  if (l.reduziert) {
    return ta("Das Portal deiner Agentur ist gesperrt.", "Your agency's portal is locked.", "Dein Konto ist gesperrt.", "Your account is locked.");
  }
  return l.endeAm
    ? ta(`Seit dem ${l.endeAm} ist das Portal deiner Agentur gesperrt. Deine Daten sind vollständig da.`, `Your agency's portal has been locked since ${l.endeAm}. Your data is all still there.`,
      `Seit dem ${l.endeAm} ist dein Konto gesperrt. Deine Daten sind vollständig da.`, `Your account has been locked since ${l.endeAm}. Your data is all still there.`)
    : ta("Das Portal deiner Agentur ist gesperrt. Deine Daten sind vollständig da.", "Your agency's portal is locked. Your data is all still there.",
      "Dein Konto ist gesperrt. Deine Daten sind vollständig da.", "Your account is locked. Your data is all still there.");
}

/** Zweiter Satz: Export-Phase bis wann und was danach passiert; nach der Reduktion die aufbewahrten Belege */
export function exportSatz(l: SperreLage): string {
  if (l.reduziert) {
    return t(`Die Export-Phase ist vorbei. Aufbewahrt werden nur noch die Belege, die aufbewahrt werden müssen${l.belegeBis ? `, bis ${l.belegeBis}` : ""}. Du kannst sie hier herunterladen.`,
      `The export phase is over. Only the records that must be retained are still kept${l.belegeBis ? `, until ${l.belegeBis}` : ""}. You can download them here.`);
  }
  const bis = l.exportBis
    ? t(`Bis ${l.exportBis} kannst du alle Daten und Belege exportieren (Export-Phase).`, `Until ${l.exportBis} you can export all data and records (export phase).`)
    : t("Mindestens 90 Tage lang kannst du alle Daten und Belege exportieren (Export-Phase).", "For at least 90 days you can export all data and records (export phase).");
  const danach = l.loeschungBeantragt
    ? t(" Auf deinen Wunsch löschen wir danach alle Daten, mit Ankündigung per Mail.", " At your request we then delete all data, with notice by email.")
    : l.allesLoeschen
    ? t(" Danach löschen wir alle Daten, mit Ankündigung per Mail.", " After that we delete all data, with notice by email.")
    : t(" Danach löschen wir alles außer den Belegen, die aufbewahrt werden müssen, mit Ankündigung per Mail.", " After that we delete everything except the records that must be retained, with notice by email.");
  return bis + danach;
}

/** S-1: eine Zeile für Abo & Tarif hinter der Sperre: beendet am …, Export bis … bzw. Belege bis … */
export function beendetZeile(l: SperreLage): string {
  const ende = l.endeAm ? (l.trial ? t(`Test beendet am ${l.endeAm}.`, `Trial ended on ${l.endeAm}.`) : t(`Abo beendet am ${l.endeAm}.`, `Subscription ended on ${l.endeAm}.`))
    : (l.trial ? t("Test beendet.", "Trial ended.") : t("Abo beendet.", "Subscription ended."));
  const bis = l.reduziert
    ? (l.belegeBis ? t(` Aufbewahrte Belege abrufbar bis ${l.belegeBis}.`, ` Retained records available until ${l.belegeBis}.`) : "")
    : (l.exportBis ? t(` Export bis ${l.exportBis}.`, ` Export until ${l.exportBis}.`) : "");
  return ende + bis;
}

/** Hinweis für Teammitglieder ohne Admin-Recht: der Admin (Kontoinhaber) exportiert */
export function teamSperreSatz(orgName: string | null, exportBis: string | null): string {
  return t(`${orgName ? `Das Portal von ${orgName}` : "Das Portal deiner Agentur"} ist gesperrt. Der Admin deiner Agentur (Kontoinhaber) kann alle Daten und Belege ${exportBis ? `bis ${exportBis} ` : ""}exportieren; wende dich bei Fragen an deinen Admin.`,
    `${orgName ? `The ${orgName} portal` : "Your agency's portal"} is locked. Your agency's admin (account owner) can export all data and records${exportBis ? ` until ${exportBis}` : ""}; please contact your admin with any questions.`);
}

export function teamSperreTitel(reason: string | null | undefined, trial = false): string {
  return trial || reason === "trial_expired" ? t("Der Test deiner Agentur ist beendet", "Your agency's trial has ended") : t("Das Abo deiner Agentur ist beendet", "Your agency's subscription has ended");
}

/** Creator einer beendeten Agentur (Creator-Portal, eigene Sprache des Creators) */
export function creatorSperreTexte(orgName: string, belegeBis: string | null, tc: (de: string, en: string) => string): { titel: string; text: string; knopf: string } {
  return {
    titel: tc("Deine Agentur nutzt CreatorOffice nicht mehr", "Your agency no longer uses CreatorOffice"),
    text: tc(`${orgName} hat CreatorOffice beendet, der Bereich dieser Agentur ist deshalb gesperrt. Deine eigenen Daten mit Rechnungen, Gutschriften und Verträgen lädst du unter Konto als ZIP herunter${belegeBis ? `, bis zum ${belegeBis}` : ""}.`,
      `${orgName} has stopped using CreatorOffice, so this agency's area is locked. You can download your own data including invoices, credit notes and contracts as a ZIP under Account${belegeBis ? `, until ${belegeBis}` : ""}.`),
    knopf: tc("Meine Daten herunterladen", "Download my data"),
  };
}
